/**
 * Coordinate conversion utilities for tile-based map system.
 * Converts between world coordinates (pixels) and tile coordinates (grid).
 */

import {
  TILE_SIZE,
  ISO_HORIZONTAL_SPACING,
  ISO_VERTICAL_SPACING,
  ISO_SPACING_ADJUSTMENT,
} from '@shared/constants/map';

/**
 * Tile coordinate position in the grid.
 */
export interface TilePosition {
  x: number;
  y: number;
}

/**
 * World coordinate position in pixels.
 */
export interface WorldPosition {
  x: number;
  y: number;
}

/**
 * Convert world coordinates (pixels) to tile coordinates (grid).
 *
 * @param worldX - X coordinate in pixels
 * @param worldY - Y coordinate in pixels
 * @param tileSize - Size of each tile in pixels (default: TILE_SIZE)
 * @returns Tile position in grid coordinates
 */
export function worldToTile(
  worldX: number,
  worldY: number,
  tileSize: number = TILE_SIZE
): TilePosition {
  return {
    x: Math.floor(worldX / tileSize),
    y: Math.floor(worldY / tileSize),
  };
}

/**
 * Convert tile coordinates (grid) to world coordinates (pixels).
 * Returns the top-left corner of the tile in world coordinates.
 *
 * @param tileX - X coordinate in grid
 * @param tileY - Y coordinate in grid
 * @param tileSize - Size of each tile in pixels (default: TILE_SIZE)
 * @returns World position in pixels (top-left corner of tile)
 */
export function tileToWorld(
  tileX: number,
  tileY: number,
  tileSize: number = TILE_SIZE
): WorldPosition {
  return {
    x: tileX * tileSize,
    y: tileY * tileSize,
  };
}

/**
 * Convert tile coordinates to world coordinates for the center of a tile.
 *
 * @param tileX - X coordinate in grid
 * @param tileY - Y coordinate in grid
 * @param tileSize - Size of each tile in pixels (default: TILE_SIZE)
 * @returns World position in pixels (center of tile)
 */
export function tileToWorldCenter(
  tileX: number,
  tileY: number,
  tileSize: number = TILE_SIZE
): WorldPosition {
  return {
    x: tileX * tileSize + tileSize / 2,
    y: tileY * tileSize + tileSize / 2,
  };
}

/**
 * Get the world bounds (rectangle) for a multi-tile building.
 *
 * @param tileX - Top-left X coordinate in grid
 * @param tileY - Top-left Y coordinate in grid
 * @param width - Width of building in tiles
 * @param height - Height of building in tiles
 * @param tileSize - Size of each tile in pixels (default: TILE_SIZE)
 * @returns World bounds as { x, y, width, height }
 */
export function getBuildingWorldBounds(
  tileX: number,
  tileY: number,
  width: number,
  height: number,
  tileSize: number = TILE_SIZE
): { x: number; y: number; width: number; height: number } {
  const worldPos = tileToWorld(tileX, tileY, tileSize);
  return {
    x: worldPos.x,
    y: worldPos.y,
    width: width * tileSize,
    height: height * tileSize,
  };
}

/**
 * Convert tile coordinates to isometric world coordinates.
 * Classic isometric projection: x_iso = (x - y) * tileWidth/2, y_iso = (x + y) * tileHeight/2
 *
 * @param tileX - X coordinate in grid
 * @param tileY - Y coordinate in grid
 * @param tileSize - Size of each tile in pixels (default: TILE_SIZE)
 * @returns Isometric world position in pixels
 */
export function tileToIsometricWorld(
  tileX: number,
  tileY: number,
  tileSize: number = TILE_SIZE
): WorldPosition {
  // Mathematically precise isometric projection (2:1 ratio)
  //
  // Diamond tile geometry:
  // - Texture: tileSize × tileSize/2 (width × height)
  // - Diamond points: top (tileSize/2, 0), right (tileSize, tileSize/4),
  //   bottom (tileSize/2, tileSize/2), left (0, tileSize/4)
  // - Origin: center (0.5, 0.5) = (tileSize/2, tileSize/4) in texture coordinates
  //
  // With center origin, the diamond extends:
  // - Up: tileSize/4 from center (to top point at y=0)
  // - Down: tileSize/4 from center (to bottom point at y=tileSize/2)
  // - Total vertical extent from center: tileSize/2
  //
  // For adjacent tiles (x,y) and (x,y+1) in isometric space:
  // - Vertical distance between centers = spacingFactor (since deltaY = spacingFactor)
  // - For perfect connection: spacingFactor must equal tileSize/2 (the diamond height)
  //
  // However, standard 2:1 isometric uses spacingFactor = tileHeight/2 = tileSize/4
  // This creates gaps because the diamond is tileSize/2 tall, not tileSize/4.
  //
  // The mathematically precise value: spacingFactor = tileSize/2 = tileHeight
  // But to account for sub-pixel rendering and ensure no gaps, use slightly tighter:
  // Standard isometric projection (2:1 ratio) - BEST PRACTICE
  //
  // The correct approach: Use different spacing for horizontal and vertical
  // - Horizontal spacing: tileSize / 2 (standard isometric)
  // - Vertical spacing: tileSize / 4 (standard 2:1 isometric ratio)
  //
  // Using exact integer constants to avoid floating-point precision issues.
  // For TILE_SIZE = 32: horizontal = 16, vertical = 8 (exact integers)
  // Apply fine-tuning adjustment for pixel-perfect alignment
  // If tileSize differs from TILE_SIZE, calculate from it, otherwise use constants
  const baseHorizontalSpacing =
    tileSize === TILE_SIZE ? ISO_HORIZONTAL_SPACING : Math.round(tileSize / 2);
  const baseVerticalSpacing =
    tileSize === TILE_SIZE ? ISO_VERTICAL_SPACING : Math.round(tileSize / 4);
  const horizontalSpacing = baseHorizontalSpacing + ISO_SPACING_ADJUSTMENT;
  const verticalSpacing = baseVerticalSpacing + ISO_SPACING_ADJUSTMENT;

  return {
    x: (tileX - tileY) * horizontalSpacing,
    y: (tileX + tileY) * verticalSpacing,
  };
}

/**
 * Convert isometric world coordinates to tile coordinates.
 * Inverse of isometric projection.
 *
 * @param worldX - X coordinate in isometric pixels
 * @param worldY - Y coordinate in isometric pixels
 * @param tileSize - Size of each tile in pixels (default: TILE_SIZE)
 * @returns Tile position in grid coordinates
 */
export function isometricWorldToTile(
  worldX: number,
  worldY: number,
  tileSize: number = TILE_SIZE
): TilePosition {
  // Inverse isometric projection
  // From: x = (tileX - tileY) * horizontalSpacing, y = (tileX + tileY) * verticalSpacing
  // where horizontalSpacing = tileSize / 2, verticalSpacing = tileSize / 4.4
  // Solve for tileX and tileY:
  // Using exact integer constants to match tileToIsometricWorld
  // Apply the same fine-tuning adjustment as in tileToIsometricWorld
  const baseHorizontalSpacing =
    tileSize === TILE_SIZE ? ISO_HORIZONTAL_SPACING : Math.round(tileSize / 2);
  const baseVerticalSpacing =
    tileSize === TILE_SIZE ? ISO_VERTICAL_SPACING : Math.round(tileSize / 4);
  const horizontalSpacing = baseHorizontalSpacing + ISO_SPACING_ADJUSTMENT;
  const verticalSpacing = baseVerticalSpacing + ISO_SPACING_ADJUSTMENT;
  const tileX = Math.round((worldX / horizontalSpacing + worldY / verticalSpacing) / 2);
  const tileY = Math.round((worldY / verticalSpacing - worldX / horizontalSpacing) / 2);
  return { x: tileX, y: tileY };
}
